/**
 * La numérotation du lecteur de forme (#1862) : un paragraphe numéroté tient
 * une part de sa forme du NIVEAU de sa liste (`w:numPr` → `word/numbering.xml`
 * → `w:lvl`), d'abord ses retraits.
 *
 * Mesuré sur SuperDoc 2.15.1 (2026-10-02, Word importé de référence) :
 * - tout le `w:pPr` du niveau s'applique au paragraphe — retraits, mais aussi
 *   alignement et espacement ;
 * - le `w:rPr` du niveau ne vaut que pour le NUMÉRO, jamais pour le texte : ce
 *   module ne le lit pas ;
 * - le niveau se place juste SOUS ce qui porte la numérotation : posée à la
 *   main, il passe devant tous les styles ; portée par un style, le retrait de
 *   ce style passe devant lui ;
 * - `w:ilvl` absent vaut le premier niveau ; `w:numId="0"` retire la
 *   numérotation ; une surcharge (`w:lvlOverride`) qui porte un `w:lvl`
 *   remplace le niveau de la liste ;
 * - une liste, ou un niveau, que le fichier ne définit pas est peint sans
 *   numéro ni retrait — y compris une liste qui renvoie à un style de liste
 *   (`w:numStyleLink`), que ce module ne suit pas. Ce que le fichier voulait
 *   dire n'étant pas établi, la forme du paragraphe — retraits, alignement,
 *   espacement — est rendue non lisible, hors ce qui est posé à la main.
 */

import { isWord, type XmlNode } from '@hector/docx-integrity';
import { attribut, enfant, porteur, type Niveau } from './cascadeDeForme';

/** D'où un paragraphe tient sa numérotation : de personne, de son `w:numPr` direct, ou d'un style nommé. */
export type OrigineDeNumerotation = 'aucune' | 'a_la_main' | 'du_style';

/** Pour chaque liste (`w:numId`), le `w:pPr` de chacun de ses niveaux (`w:ilvl`) ; `undefined` : le niveau n'en porte pas. */
export type NumerotationDuDocument = ReadonlyMap<string, ReadonlyMap<string, XmlNode | undefined>>;

function niveauxDe(conteneur: XmlNode): Array<[string, XmlNode | undefined]> {
  return conteneur.children.flatMap((niveau): Array<[string, XmlNode | undefined]> => {
    const rang = isWord(niveau, 'lvl') ? attribut(niveau, 'ilvl') : undefined;
    return rang === undefined ? [] : [[rang, enfant(niveau, 'pPr')]];
  });
}

/**
 * Lit les listes du fichier.
 *
 * @param racine La racine de `word/numbering.xml`, ou `undefined` si le fichier n'en a pas.
 */
export function lireLaNumerotation(racine: XmlNode | undefined): NumerotationDuDocument {
  const modeles = new Map<string, Array<[string, XmlNode | undefined]>>();
  const listes = new Map<string, Map<string, XmlNode | undefined>>();
  for (const noeud of racine?.children ?? []) {
    const modele = isWord(noeud, 'abstractNum') ? attribut(noeud, 'abstractNumId') : undefined;
    if (modele !== undefined) modeles.set(modele, niveauxDe(noeud));
  }
  for (const noeud of racine?.children ?? []) {
    const liste = isWord(noeud, 'num') ? attribut(noeud, 'numId') : undefined;
    if (liste === undefined) continue;
    const niveaux = new Map(modeles.get(attribut(enfant(noeud, 'abstractNumId'), 'val') ?? ''));
    for (const surcharge of noeud.children) {
      if (isWord(surcharge, 'lvlOverride')) for (const [rang, pPr] of niveauxDe(surcharge)) niveaux.set(rang, pPr);
    }
    listes.set(liste, niveaux);
  }
  return listes;
}

/**
 * Place le niveau de liste dans la cascade d'un paragraphe.
 *
 * @param niveaux Les niveaux de paragraphe, du plus fort au plus faible, sans la numérotation.
 * @returns Les niveaux, niveau de liste inséré ; `introuvable` quand le
 *   paragraphe est numéroté mais que le fichier ne définit pas son niveau ;
 *   `numerotation` : ce qui porte la numérotation — la forme directe (le
 *   premier niveau reçu), un niveau en dessous d'elle, ou rien.
 */
export function placerLeNiveauDeListe(
  niveaux: readonly Niveau[],
  numerotation: NumerotationDuDocument,
): { niveaux: readonly Niveau[]; introuvable: boolean; numerotation: OrigineDeNumerotation } {
  for (const [rang, niveau] of niveaux.entries()) {
    const marque = niveau.sorte === 'porteur' ? enfant(niveau.proprietes, 'numPr') : undefined;
    if (!marque) continue;
    const liste = attribut(enfant(marque, 'numId'), 'val');
    if (liste === '0') break;
    const origine = niveau.origine === 'direct' ? 'a_la_main' as const : 'du_style' as const;
    const niveauxDeLaListe = liste === undefined ? undefined : numerotation.get(liste);
    const niveauDeListe = attribut(enfant(marque, 'ilvl'), 'val') ?? '0';
    if (!niveauxDeLaListe?.has(niveauDeListe)) return { niveaux, introuvable: true, numerotation: origine };
    return {
      niveaux: [...niveaux.slice(0, rang + 1), porteur('style', niveauxDeLaListe.get(niveauDeListe)), ...niveaux.slice(rang + 1)],
      introuvable: false,
      numerotation: origine,
    };
  }
  return { niveaux, introuvable: false, numerotation: 'aucune' };
}
